import { ArrowLeft, Loader2, Lock } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

import { hora, tempoAberto } from "../lib/caixa";
import AvisoEmAberto from "./AvisoEmAberto";
import PassoContagem from "./PassoContagem";
import PassoResultado from "./PassoResultado";
import useFechamento from "./useFechamento";

function Passos({ atual }) {
  const passo = (n, rotulo) => (
    <span className={`flex items-center gap-1.5 ${atual === n ? "text-foreground font-semibold" : "text-muted-foreground"}`}>
      <span className={`w-5 h-5 rounded-full text-[11px] flex items-center justify-center ${atual >= n ? "bg-primary text-primary-foreground" : "bg-muted"}`}>{n}</span>{rotulo}
    </span>
  );
  return <div className="flex items-center gap-3 text-xs">{passo(1, "Contar")}<span className="w-6 h-px bg-border" />{passo(2, "Conferir e fechar")}</div>;
}

/**
 * Fechamento de caixa (v1.5.784): 1) contagem às cegas por forma; 2) o sistema
 * mostra esperado × contado; se não bater, pede o motivo antes de fechar.
 * Usado pela tela Caixa e pelo botão "Caixa Aberto" do cabeçalho.
 * Com pedido ou mesa em aberto na loja (22/10/2026) a lista aparece em vermelho
 * com "Ir até ele" (AvisoEmAberto). Desde 25/10/2026 (dono) a janela mostra SÓ
 * esse aviso: sem contagem, sem passos e sem Conferir.
 */
export default function FechamentoCaixaDialog({ open, onOpenChange, caixaAberto, onFechado }) {
  const f = useFechamento({ open, caixaId: caixaAberto?.id, onFechado: (c) => { onFechado?.(c); onOpenChange(false); } });
  const passo = f.resultado ? 2 : 1;
  const precisaMotivo = f.resultado?.resultado === "com_diferenca" && !f.justificativa.trim();
  const travado = f.dados.emAbertoPronto && f.temEmAberto;
  const pronto = f.dados.emAbertoPronto && !!f.dados.resumo;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl p-0 gap-0 overflow-hidden" data-e2e="fechar-caixa">
        <div className="px-5 pt-5 pb-3 border-b space-y-2">
          <div className="flex items-center gap-2"><Lock className="w-5 h-5 text-primary" /><DialogTitle className="text-lg font-bold">Fechar caixa</DialogTitle></div>
          <p className="text-xs text-muted-foreground">
            {caixaAberto?.usuario_nome} · aberto às {hora(caixaAberto?.opened_at)} ({tempoAberto(caixaAberto?.opened_at)})
          </p>
          {!travado && <Passos atual={passo} />}
        </div>
        <div className="p-5 max-h-[65vh] overflow-y-auto">
          <AvisoEmAberto emAberto={f.dados.emAberto} aoIr={() => onOpenChange(false)} />
          {travado ? null
            : !pronto ? <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
            : passo === 1 ? <PassoContagem f={f} /> : <PassoResultado f={f} />}
        </div>
        <div className="px-5 py-3 border-t flex items-center gap-2">
          {passo === 2 && <Button variant="outline" onClick={f.voltar} disabled={f.ocupado}><ArrowLeft className="w-4 h-4 mr-1" />Recontar</Button>}
          <Button variant={travado ? "outline" : "ghost"} className={travado ? "ml-auto" : ""} onClick={() => onOpenChange(false)} disabled={f.ocupado} data-e2e="fechar-caixa-cancelar">
            {travado ? "Entendi" : "Cancelar"}
          </Button>
          {travado ? null : passo === 1 ? (
            <Button className="ml-auto h-11 px-6 font-semibold" onClick={f.conferir} disabled={f.ocupado || !pronto || f.temEmAberto} data-e2e="conferir-caixa"
              title={f.temEmAberto ? "Resolva o que está em aberto antes de fechar o caixa" : ""}>
              {f.ocupado ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}Conferir
            </Button>
          ) : (
            <Button className="ml-auto h-11 px-6 font-semibold" onClick={() => f.fechar()} disabled={f.ocupado || precisaMotivo} data-e2e="confirmar-fechamento"
              title={precisaMotivo ? "Escreva o motivo da diferença" : ""}>
              {f.ocupado ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Lock className="w-4 h-4 mr-2" />}Fechar caixa
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
